import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Typography, CircularProgress, Alert } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import getBrand from "../brands/getBrand";
import CustomAxios from "../utils/CustomAxios";

const OrderTrackingPage = () => {
  const brand = getBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const reduxUserId = useSelector((state) => state.user.id);
  // Get userId from query params, navigation state, or Redux (in that order)
  const userIdFromQuery = searchParams.get('user_id');
  const userIdFromState = location.state?.user_id || location.state?.order?.user_id || location.state?.order?.user?.id;
  const userId = userIdFromQuery || userIdFromState || reduxUserId;
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setError("Order ID is missing");
      setLoading(false);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        
        // If userId is not available, try to get it from the order itself
        let orderUserId = userId;
        
        if (!orderUserId) {
          // For admin users: try to get user_id from order by making a call
          // The backend should return the order's user_id in the response
          // We'll try with 0 as placeholder and extract user_id from response
          try {
            const testResponse = await CustomAxios.get(
              `check_payment_status/${orderId}/0/`
            );
            // Extract user_id from the response if available
            if (testResponse.data?.user?.id) {
              orderUserId = testResponse.data.user.id;
              // Now fetch again with the correct user_id
              const response = await CustomAxios.get(
                `check_payment_status/${orderId}/${orderUserId}/`
              );
              setOrderData(response.data);
              setError(null);
              setLoading(false);
              return;
            }
          } catch (testErr) {
            // If that fails, show error
            console.error("Error fetching order details:", testErr);
            setError(
              testErr?.response?.data?.error || 
              "Failed to fetch order details. User ID is required. Please provide user_id as a query parameter (?user_id=XXX) or ensure you're logged in."
            );
            setLoading(false);
            return;
          }
        }

        // Normal flow with userId available
        const response = await CustomAxios.get(
          `check_payment_status/${orderId}/${orderUserId}/`
        );
        setOrderData(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError(
          err?.response?.data?.error || "Failed to fetch order details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, userId]);

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "#F59E0B",
      confirmed: "#3B82F6",
      "delivery_partner_assigned": "#8B5CF6",
      "delivery_partner_accepted": "#8B5CF6",
      "order_picked_up": "#10B981",
      delivered: "#10B981",
      cancelled: "#EF4444",
    };
    return statusColors[status] || "#6B7280";
  };

  const formatStatus = (status) => {
    return status
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || "Unknown";
  };

  const orderDetails = orderData?.order_details || {};
  console.log('order details---------------->',orderDetails);
  console.log('order full data---------------->',orderData);
  const TransactionFee = (() => {
    const total = parseFloat(orderDetails.total_amount ?? 0) || 0;
    const amount = parseFloat(orderDetails.amount ?? 0) || 0;
    const tax = parseFloat(orderDetails.tax ?? 0) || 0;
    const delivery = parseFloat(orderDetails.delivery_charge ?? 0) || 0;
    const fee = total - (amount + tax + delivery);
    // Avoid negative due to rounding / missing fields
    return fee > 0 ? fee : 0;
  })();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: brand?.backgroundColor || "#F9FAFB",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: { xs: 2, md: 3 },
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          height: { xs: "56px", md: "72px" },
        }}
      >
        <Button
          startIcon={
            <ArrowBackIosIcon
              sx={{ fontSize: "14px", color: brand?.theme?.textPrimary || "#111827" }}
            />
          }
          onClick={() => navigate("/home/dashboard")}
          sx={{ textTransform: "none" }}
        />
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>
          Order Tracking
        </Typography>
        <Box />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, flex: 1 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress sx={{ color: brand?.themeColor || "#1976d2" }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : orderData ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Order Status Card */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "20px",
                border: `2px solid ${getStatusColor(orderData.status)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6B7280",
                  marginBottom: "8px",
                }}
              >
                Order Status
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: getStatusColor(orderData.status),
                }}
              >
                {formatStatus(orderData.status)}
              </Typography>
            </Box>

            {/* Ordered Products Card */}
            {orderData.details?.products?.length > 0 && (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                  Ordered Items
                </Typography>

                {orderData.details.products.map((product, index) => {

                  // ✅ Match by product_id
                  const productInfo = orderData.details.product_details?.find(
                    (p) => p.id === product.product_id
                  );

                  // Find selected capacity
                  const selectedCapacity = product.capacity?.find(
                    (cap) => cap.item_quantity && cap.item_quantity > 0
                  );

                  // Skip if nothing selected
                  if (!selectedCapacity) return null;

                  const imageUrl = productInfo?.images?.[0];

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid #F3F4F6",
                        paddingBottom: "12px",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {productInfo?.item_name || "Product"}
                        </Typography>

                        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                          Capacity: {selectedCapacity.capacity}
                        </Typography>

                        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                          Quantity: {selectedCapacity.item_quantity}
                        </Typography>
                      </Box>

                      {imageUrl && (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={productInfo?.item_name}
                          sx={{
                            width: 70,
                            height: 70,
                            objectFit: "cover",
                            borderRadius: "8px",
                            ml: 2,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {/* Order Details Card */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}
              >
                Order Details
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                    Order ID
                  </Typography>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                  >
                    {orderData.order_id}
                  </Typography>
                </Box>

                {orderData.order_details?.total_amount && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Total Amount
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                    >
                      ₹{parseFloat(orderData.order_details.total_amount).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {orderData.order_details?.amount && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Subtotal
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                    >
                      ₹{parseFloat(orderData.order_details.amount).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {orderData.order_details?.tax && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Tax
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                    >
                      ₹{parseFloat(orderData.order_details.tax || 0).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {TransactionFee > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Transaction fee (2.9%)
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                    >
                      ₹{TransactionFee.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {orderData.order_details?.delivery_charge !== undefined && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Delivery Charge
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}
                    >
                      ₹{parseFloat(orderData.order_details.delivery_charge || 0).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Delivery/Pickup Slot Card */}
            {orderData.delivery_slot && (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}
                >
                  {orderData.delivery_slot.slot_type === 'pickup' 
                    ? 'Scheduled Pickup Slot' 
                    : 'Scheduled Delivery Slot'}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {orderData.delivery_slot.delivery_date_formatted && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        Date:
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                        {orderData.delivery_slot.delivery_date_formatted}
                      </Typography>
                    </Box>
                  )}
                  {orderData.delivery_slot.day_name && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        Day:
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                        {orderData.delivery_slot.day_name}
                      </Typography>
                    </Box>
                  )}
                  {orderData.delivery_slot.time_range && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        Time:
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                        {orderData.delivery_slot.time_range}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Delivery Address Card */}
            {orderData.delivery && (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}
                >
                  Delivery Address
                </Typography>
                {typeof orderData.delivery === "object" ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {orderData.delivery.name && (
                      <Typography sx={{ fontSize: "14px", color: "#111827" }}>
                        {orderData.delivery.name}
                      </Typography>
                    )}
                    {orderData.delivery.address_line1 && (
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        {orderData.delivery.address_line1}
                      </Typography>
                    )}
                    {(orderData.delivery.city ||
                      orderData.delivery.state ||
                      orderData.delivery.pincode) && (
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        {[
                          orderData.delivery.city,
                          orderData.delivery.state,
                          orderData.delivery.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    )}
                    {orderData.delivery.mobileNo && (
                      <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                        {orderData.delivery.mobileNo}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                    {orderData.delivery}
                  </Typography>
                )}
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/home/dashboard")}
                sx={{
                  textTransform: "none",
                  flex: 1,
                  borderColor: brand?.themeColor || "#1976d2",
                  color: brand?.themeColor || "#1976d2",
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default OrderTrackingPage;

